import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { appendRowNodeIdToInputAriaLabel, suppressTabbingToNextCell, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { ColDef, GridOptions, SelectionChangedEvent } from "ag-grid-community";
import { forkJoin } from "rxjs";
import {
  ExportFormat,
  GroupStorage,
  HighAvailabilityModes,
  IConfigurationHolder,
  IDirectoryLoginInfo,
  ISharePointLoginInfo,
  IUser,
  IUserLicenseInfo,
  IUserPair,
  UserMappingOperation
} from "../../../../models/auto-generated";
import { MappingStatus } from "../../../../models/mapping-status";
import { UserMappingDto } from "../../../../models/user-mapping-dto";
import { AUserPermissionsService } from "../../../../services/a-user-permissions.service";
import { LicenseService } from "../../../../services/license.service";
import { ReplicationService } from "../../../../services/replication.service";
import { SettingsService } from "../../../../services/settings.service";
import { UserMappingService } from "../../../../services/user-mapping.service";
import { handleError } from "../../../../utils/handle-error";
import { isRunning } from "../../../../utils/is-running";
import { SharePointUser } from "../models/sharepoint-user";
import { SharePointUserType } from "../models/sharepoint-user-type";
import { CustomUserMappingComponent } from "./custom-user-mapping/custom-user-mapping.component";
import { UserMappingSettingsComponent } from "./user-mapping-settings/user-mapping-settings.component";
import { saveAsWithTimestamp } from "libs/core/src/lib/utils";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./user-mapping.component.html",
  styleUrls: ["./user-mapping.component.scss"]
})
export class UserMappingComponent implements OnInit {
  @ViewChild("actionButtons", { static: true }) actionButtons: TemplateRef<any>;
  GroupStorage = GroupStorage;
  MappingStatus = MappingStatus;

  editModalRef: NgbModalRef;
  settingsModalRef: NgbModalRef;

  errorMessage: string;
  successMessage: string;
  instanceId: string;
  spConfigIndex: number;
  sharePointId: number;
  isReadOnly: boolean;
  licenseInfo: IUserLicenseInfo;
  numberOfMappedUsers = 0;
  currentSettings: IConfigurationHolder;
  isFailoverBackupInstance: boolean;
  isRunning: boolean;

  gridOptions: GridOptions;
  rowData: UserMappingDto[] = [];
  selectedRows: UserMappingDto[];

  availableUsers: SharePointUser[];
  userPairs: IUserPair[];
  columnDefs: ColDef[];

  /** Returns true if a Block operation is allowed for selected user mapping(s); otherwise false. */
  isBlockAllowed: boolean;
  /** Returns true if a Unblock operation is allowed for selected user mapping(s); otherwise false. */
  isUnblockAllowed: boolean;
  /** Returns true if a Remove operation is allowed for selected user mapping(s); otherwise false. */
  isRemoveAllowed: boolean;
  constructor(
    @Inject(IS_SAAS)
    public isSaaS: boolean,
    public loadingService: LoadingService,
    private route: ActivatedRoute,
    private replicationService: ReplicationService,
    private userPermissionsService: AUserPermissionsService,
    private settingsService: SettingsService,
    private userMappingService: UserMappingService,
    private modalService: NgbModal,
    private licenseService: LicenseService
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.params["instance-id"];
    this.sharePointId = this.route.snapshot.params["sharepoint-id"];
    this.gridOptions = {
      rowHeight: 40,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      pagination: true,
      paginationPageSize: 50,
      suppressPaginationPanel: true,
      isRowSelectable: this.isRowSelectable.bind(this),
      onSelectionChanged: this.onSelectionChanged.bind(this),
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of User Mappings");
        e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
      },
      defaultColDef: { sortable: true, resizable: true, minWidth: 100, suppressKeyboardEvent: suppressTabbingToNextCell }
    };
    this.loadLicenseInfo();
    this.loadInformation();
  }

  openSettingsModal() {
    this.successMessage = undefined;
    this.settingsModalRef = this.modalService.open(UserMappingSettingsComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.settingsModalRef.componentInstance.isReadOnly = this.isReadOnly;
    this.settingsModalRef.componentInstance.groupStorage = this.currentSettings.groupStorage;
    this.settingsModalRef.componentInstance.currentSettings = {
      mappingMode: this.currentSettings.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.mode,
      autoRefresh: this.currentSettings.userMapping_AutoRefresh,
      refreshInterval: this.currentSettings.userMapping_RefreshInterval
    };
    this.settingsModalRef.componentInstance.saveCallback = (newValue: any) => {
      const newSettings = Object.assign({}, this.currentSettings);
      newSettings.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.mode = newValue.mappingMode;
      newSettings.userMapping_AutoRefresh = newValue.autoRefresh;
      newSettings.userMapping_RefreshInterval = newValue.refreshInterval;
      this.updateSettings(
        newSettings,
        () => {
          this.settingsModalRef.close();
          this.successMessage = `Settings have been successfully saved.`;
        },
        (error) => (this.settingsModalRef.componentInstance.errorMessage = `Failed to update settings due to: ${handleError(error)}`)
      );
    };
  }

  openEditModal(params: any) {
    this.successMessage = undefined;
    this.editModalRef = this.modalService.open(CustomUserMappingComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.editModalRef.componentInstance.userMappings = this.rowData;
    this.editModalRef.componentInstance.userMappingToEdit = params.data;
    this.editModalRef.componentInstance.users = this.availableUsers;
    this.editModalRef.componentInstance.mappingMode = this.currentSettings.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.mode;
    this.editModalRef.componentInstance.userPairsToEdit = Object.assign(
      [],
      this.userPairs.filter((item) => item.crM_ID === params.data.crmId)
    );
    this.editModalRef.componentInstance.saveCallback = (newPairs: IUserPair[]) => {
      const userPairsWoCurrentUser = this.userPairs.filter((item) => item.crM_ID !== params.data.crmId);
      this.userPairs = userPairsWoCurrentUser.concat(newPairs);
      this.saveUserMapping(
        () => {
          this.successMessage = `User mapping has been successfully updated.`;
          this.editModalRef.close();
        },
        (error) => (this.editModalRef.componentInstance.errorMessage = `Failed to update user mapping due to: ${handleError(error)}`)
      );
    };
  }

  blockSelected() {
    this.selectedRows.forEach((selectedUser) => {
      const loginInfo = selectedUser.loginInfo as ISharePointLoginInfo;
      const userPair: IUserPair = {
        crM_ID: selectedUser.crmId,
        email: selectedUser.email,
        name: selectedUser.name,
        operation: UserMappingOperation.Block,
        sharePoint_ID: loginInfo ? loginInfo.id : 0
      };
      this.userPairs.push(userPair);
    });
    this.selectedRows = [];
    this.saveUserMapping(
      () => (this.successMessage = `User mapping(s) have been successfully blocked.`),
      (error) => (this.errorMessage = `Failed to block user mapping(s) due to: ${handleError(error)}`)
    );
  }

  removeSelected() {
    this.selectedRows.forEach((u) => {
      let index: number;
      if (this.currentSettings.groupStorage === GroupStorage.ActiveDirectory || this.currentSettings.groupStorage === GroupStorage.AzureActiveDirectory) {
        index = this.userPairs.findIndex((pair) => pair.crM_ID === u.crmId);
      } else {
        const loginInfo = u.loginInfo as ISharePointLoginInfo;
        index = this.userPairs.findIndex((pair) => pair.sharePoint_ID === loginInfo.id && pair.crM_ID === u.crmId);
      }
      if (index > -1) {
        this.userPairs.splice(index, 1);
      }
    });
    this.selectedRows = [];
    this.saveUserMapping(
      () => (this.successMessage = `User mapping(s) have been successfully ${this.isRemoveAllowed ? "deleted" : "unblocked"}.`),
      (error) => (this.errorMessage = `Failed to  ${this.isRemoveAllowed ? "delete" : "unblock"} user mapping(s) due to: ${handleError(error)}`)
    );
  }

  private isRowSelectable() {
    return !this.isReadOnly;
  }

  private onSelectionChanged(event: SelectionChangedEvent) {
    event.api.forEachNode(appendRowNodeIdToInputAriaLabel);
    const rows = event.api.getSelectedRows();
    this.selectedRows = Object.assign([], rows);
    // Enable or disable operation(s) for selected user mapping(s)
    this.isBlockAllowed = this.isOperationAllowed(false, MappingStatus.Mapped);
    this.isUnblockAllowed = this.isOperationAllowed(true, MappingStatus.Blocked);
    this.isRemoveAllowed = this.isOperationAllowed(true, MappingStatus.Mapped);
  }

  private isOperationAllowed(isCustom: boolean, status: MappingStatus) {
    return (
      this.selectedRows &&
      this.selectedRows.length > 0 &&
      this.selectedRows.every((u) => {
        if (this.currentSettings.groupStorage === GroupStorage.ActiveDirectory || this.currentSettings.groupStorage === GroupStorage.AzureActiveDirectory) {
          const loginInfo = u.loginInfo as IDirectoryLoginInfo;
          if (loginInfo && loginInfo.isCustomMapping !== null) {
            return loginInfo && loginInfo.isCustomMapping === isCustom && u.status === status;
          }
          return u.status === status;
        } else {
          const loginInfo = u.loginInfo as ISharePointLoginInfo;
          return loginInfo && loginInfo.isCustomMapping === isCustom && u.status === status;
        }
      })
    );
  }

  private saveUserMapping(successCallback: () => void = null, errorCallback: (error: any) => void = null) {
    this.successMessage = this.errorMessage = undefined;
    const newSettings = Object.assign({}, this.currentSettings);
    newSettings.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.userPairs = this.userPairs;
    this.updateSettings(newSettings, successCallback, errorCallback);
  }

  private updateSettings(newValue: IConfigurationHolder, successCallback: () => void = null, errorCallback: (error: any) => void = null) {
    this.loadingService.start("Saving user mapping", "saveSettings");
    this.settingsService.save(this.instanceId, newValue).subscribe(
      (data) => {
        this.currentSettings = data;
        this.userPairs = Object.assign([], data.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.userPairs);
        if (successCallback) successCallback();
        this.loadLicenseInfo();
        this.loadUserMappings();
        this.loadingService.stop("saveSettings");
        this.isUnblockAllowed = this.isRemoveAllowed = this.isBlockAllowed = false;
      },
      (error) => {
        if (errorCallback) errorCallback(error);
        this.loadingService.stop("saveSettings");
      }
    );
  }

  private loadUserMappings() {
    this.loadingService.start("Loading user mapping", "loadUserMappings");
    this.userMappingService.get(this.instanceId, this.sharePointId).subscribe(
      (users) => {
        this.selectedRows = [];
        this.rowData = this.mapFromUserArray(users);
        this.numberOfMappedUsers = this.rowData.filter((m) => m.status === MappingStatus.Mapped).length;
        this.loadingService.stop("loadUserMappings");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadUserMappings");
      }
    );
  }

  private mapFromUserArray(users: IUser[]): UserMappingDto[] {
    const result = [];
    users.forEach((source, _index, _array) => {
      const destination = { crmId: source.crM_ID, email: source.email, isDisabled: source.isDisabled, isLicensed: source.isLicensed, loginName: source.loginName, name: source.name };
      if (this.currentSettings.groupStorage === GroupStorage.SharePoint) {
        const loginInfos = source.sharePointIDs ? source.sharePointIDs[this.sharePointId] : null;
        const blockedLoginInfos = source.sharePointBlockedIDs ? source.sharePointBlockedIDs[this.sharePointId] : null;
        if (loginInfos != null || blockedLoginInfos != null) {
          if (loginInfos != null) {
            loginInfos.forEach((l) => result.push({ ...destination, status: MappingStatus.Mapped, loginInfo: l }));
          }
          if (blockedLoginInfos != null) {
            blockedLoginInfos.forEach((l) => result.push({ ...destination, status: MappingStatus.Blocked, loginInfo: l }));
          }
        } else {
          const loginInfo: ISharePointLoginInfo = { loginName: null, id: null, isCustomMapping: null, name: null, isDeleted: null };
          result.push({ ...destination, status: MappingStatus.NotMapped, loginInfo: loginInfo });
        }
      } else {
        const loginInfos = source.directoryIDs;
        const isMapped = loginInfos != null && loginInfos.length > 0;
        const isBlocked = this.userPairs.findIndex((pair) => pair.operation === UserMappingOperation.Block && pair.crM_ID === source.crM_ID) > -1;
        result.push({
          ...destination,
          status: isBlocked ? MappingStatus.Blocked : isMapped ? MappingStatus.Mapped : MappingStatus.NotMapped,
          loginInfo: {
            loginName: isMapped ? loginInfos[0].loginName : null,
            isCustomMapping: isMapped ? loginInfos[0].isCustomMapping : null,
            name: isMapped ? loginInfos[0].name : null
          }
        });
      }
    });
    return result;
  }

  private loadLicenseInfo() {
    this.loadingService.start("Loading user license information", "loadLicenseInfo");
    this.licenseService.getUserLicenseInfo(this.instanceId).subscribe(
      (data) => {
        this.licenseInfo = data;
        this.loadingService.stop("loadLicenseInfo");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadLicenseInfo");
      }
    );
  }

  private loadInformation() {
    this.loadingService.start("Loading user mapping information", "loadInformation");
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.settingsService.get(this.instanceId)]).subscribe(
      ([status, hasLimitedAccess, currentSettings]) => {
        this.isReadOnly = hasLimitedAccess;
        this.isRunning = isRunning(status);
        if (this.isSaaS === false) {
          this.isFailoverBackupInstance = currentSettings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.isReadOnly = this.isReadOnly || this.isFailoverBackupInstance || this.isRunning;
        }
        // eslint-disable-next-line eqeqeq
        this.spConfigIndex = currentSettings.sharePoint.sharepointConfig.findIndex((element) => element.key == this.sharePointId);
        this.userPairs = Object.assign([], currentSettings.sharePoint.sharepointConfig[this.spConfigIndex].userMapping.userPairs);
        this.columnDefs = [
          {
            headerName: "Status",
            field: "status",
            checkboxSelection: true,
            valueGetter: (params) => {
              switch (params.data.status) {
                case MappingStatus.Mapped:
                  return "Mapped";
                case MappingStatus.NotMapped:
                  return "Not Mapped";
                case MappingStatus.Blocked:
                  return "Blocked";
              }
            },
            cellRenderer: (params) => {
              switch (params.value) {
                case "Mapped":
                  return `<span class="badge badge-success"><span class="fa fa-check mr-1" aria-hidden="true"></span>Mapped</span>`;
                case "Blocked":
                  return `<span class="badge badge-danger"><span class="fa fa-ban mr-1" aria-hidden="true"></span>Blocked</span>`;
                case "Not Mapped":
                  return `<span class="badge badge-warning"><span class="fa fa-exclamation-triangle mr-1" aria-hidden="true"></span>Not Mapped</span>`;
              }
            }
          },
          {
            headerName: "Custom",
            field: "loginInfo.isCustomMapping",
            valueGetter: (params) => (params.data.loginInfo.isCustomMapping ? "Yes" : "No")
          },
          { headerName: "Dynamics 365 Login", field: "loginName" },
          { headerName: "Dynamics 365 Name", field: "name" },
          {
            headerName: `${currentSettings.groupStorage === GroupStorage.SharePoint ? "SharePoint" : "Directory"} Login`,
            field: "loginInfo.loginName",
            cellRenderer: (params) => (params.value ? params.value : "-")
          },
          {
            headerName: `${currentSettings.groupStorage === GroupStorage.SharePoint ? "SharePoint" : "Directory"} Title`,
            field: "loginInfo.name",
            cellRenderer: (params) => (params.value ? params.value : "-")
          },
          {
            headerName: "Actions",
            minWidth: 80,
            maxWidth: 80,
            sortable: false,
            resizable: false,
            cellRenderer: TemplateRendererComponent,
            cellRendererParams: { ngTemplate: this.actionButtons }
          }
        ];
        if (currentSettings.groupStorage === GroupStorage.SharePoint) this.loadAvailableUsers();
        this.currentSettings = currentSettings;
        this.loadUserMappings();
        this.loadingService.stop("loadInformation");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadInformation");
      }
    );
  }

  private loadAvailableUsers() {
    this.loadingService.start("Loading available users", "loadAvailableUsers");
    this.userMappingService.getUsers(this.instanceId, this.sharePointId).subscribe(
      (data) => {
        this.availableUsers = [];
        for (const user of data.users) {
          this.availableUsers.push({ ...user, type: SharePointUserType.User });
        }
        for (const user of data.sharePointGroups) {
          this.availableUsers.push({ ...user, type: SharePointUserType.SharePointGroup });
        }
        for (const user of data.directoryGroups) {
          this.availableUsers.push({ ...user, type: SharePointUserType.DirectoryGroup });
        }
        this.loadingService.stop("loadAvailableUsers");
      },
      (error) => {
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadAvailableUsers");
      }
    );
  }

  exportUserMappingToCsv(): void {
    this.exportUserMapping(ExportFormat.Csv);
  }

  exportUserMappingToJson(): void {
    this.exportUserMapping(ExportFormat.Json);
  }

  exportUserMappingToXml(): void {
    this.exportUserMapping(ExportFormat.Xml);
  }

  private exportUserMapping(format: ExportFormat): void {
    var formatStr = ExportFormat[format];
    this.loadingService.start(`Exporting user mapping to ${formatStr} format`);
    this.userMappingService.export(this.instanceId, this.sharePointId, format).subscribe(
      (data) => {
        saveAsWithTimestamp(data, `user_mapping_${this.instanceId}_${this.sharePointId}_`, formatStr.toLocaleLowerCase());
        this.loadingService.stop();
      },
      (error) => {
        this.errorMessage = `Failed to export user mapping to ${formatStr} due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }
}
